import { useId } from 'react'
import { Moon, Sun } from 'lucide-react'
import { useAppearance, isSharedDevice, type Appearance } from '@/lib/appearance'

const OPTIONS: { value: Appearance; label: string; Icon: typeof Sun }[] = [
  { value: 'dark',  label: 'Dark',  Icon: Moon },
  { value: 'light', label: 'Light', Icon: Sun },
]

/**
 * The "Appearance" part of the account menu: Dark / Light, per device.
 * Meant to sit INSIDE a role="menu": a group of two menuitemradio rows,
 * each a 44px tap target. `onChosen` lets the menu close after a pick.
 */
export default function AppearanceRow({ onChosen }: { onChosen?: () => void }) {
  const [current, set] = useAppearance()
  const labelId = useId()
  // A shared device stays dark whatever is saved and offers no choice, so one worker cannot set it for everyone.
  if (isSharedDevice()) return null
  return (
    <>
    <div role="group" aria-labelledby={labelId}>
      <p id={labelId} className="text-caption uppercase tracking-wider text-fg-muted px-3 py-1">
        Appearance
      </p>
      <div className="flex gap-2 px-3 pb-2">
        {OPTIONS.map(({ value, label, Icon }) => {
          const on = current === value
          return (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={on}
              onClick={() => { set(value); onChosen?.() }}
              className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] rounded-lg border hairline-strong text-sm outline-none focus:bg-raised focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-500 ${
                on ? 'bg-raised text-fg font-semibold' : 'text-fg-secondary hover:bg-raised/40'
              }`}>
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          )
        })}
      </div>
    </div>
    <div role="separator" className="border-t hairline-strong my-1" />
    </>
  )
}
